import type { ColumnDef, Row, Table } from '@tanstack/react-table';
import { isUndefined } from 'lodash';
import * as React from 'react';
import { SELECTION_COLUMN_WIDTH_PX } from '../../data-grid.constants';
import type { DataGridActionDisabledReason, DataGridBaseRow } from '../../data-grid.type';
import { type IItemsCounts } from '../../hooks/use-api';
import { isRowSelected, resolveRowSelectionDisabled } from '../../util/rows';
import { Checkbox } from '@components/atoms';

interface CreateSelectionColumnProps<T> {
	handleSelectRow: (rowId: string | number, isSelected: boolean) => void;
	handleSelectAllRows?: (isSelected: boolean) => void;
	isAllPagesSelected: boolean;
	meta: IItemsCounts;
	pagination: { pageIndex: number };
	selectedRows: Record<string, Record<string, boolean>>;
	selectedRowCount: number;
	disableRowSelection?: boolean | ((row: T) => boolean | DataGridActionDisabledReason);
	uniqueField: keyof T;
}

export function createSelectionColumn<T extends DataGridBaseRow>(props: CreateSelectionColumnProps<T>): ColumnDef<T> {
	const {
		handleSelectRow,
		handleSelectAllRows,
		isAllPagesSelected,
		meta,
		pagination,
		selectedRows,
		selectedRowCount,
		disableRowSelection,
		uniqueField,
	} = props;

	const pageIndex = pagination.pageIndex.toString();

	return {
		id: 'selection',
		meta: {
			selectionState: JSON.stringify({
				currentPageSelections: selectedRows[pageIndex] || {},
				isAllPagesSelected,
				selectedRowCount,
			}),
		},
		header: handleSelectAllRows
			? ({ table }: { table: Table<T> }) => {
					const totalForSelection = !isUndefined(meta.actionableTotalItems)
						? meta.actionableTotalItems
						: meta.totalItems;
					const shouldHeaderBeChecked = totalForSelection > 0 && selectedRowCount > 0;
					const indeterminate = totalForSelection > 0 && selectedRowCount < totalForSelection;

					return (
						<Checkbox
							aria-label="Select all visible rows"
							checked={shouldHeaderBeChecked}
							indeterminate={indeterminate}
							onCheckedChange={checked => {
								handleSelectAllRows(Boolean(checked));
							}}
						/>
					);
				}
			: undefined,
		cell: ({ row }: { row: Row<T> }) => {
			const { isDisabled } = resolveRowSelectionDisabled(disableRowSelection, row.original);
			const currentPageSelections = selectedRows[pageIndex] || {};

			// Determine if this specific row is selected
			const isSelected = isRowSelected({
				currentPageSelections,
				isAllPagesSelected,
				rowId: row.original[uniqueField] as string | number,
				isDisabled,
			});

			/**
			 * NOTE: This effect is necessary to ensure that the cell re-renders. For whatever reason,
			 * `selectedRows` as a prop does not trigger a re-render when the selection state changes.
			 * This effect is a workaround to ensure that the cell re-renders when the selection state changes.
			 */

			// eslint-disable-next-line react-hooks/rules-of-hooks
			React.useEffect(() => {
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [selectedRows]);

			return (
				<Checkbox
					aria-label="Select row"
					checked={isSelected}
					onClick={e => e.stopPropagation()}
					onCheckedChange={checked => {
						const isChecked = !!handleSelectAllRows && isAllPagesSelected ? checked : !isSelected;

						handleSelectRow(row.original[uniqueField] as number | string, Boolean(isChecked));
					}}
					disabled={isDisabled}
					className="align-middle"
				/>
			);
		},
		enableResizing: false,
		size: SELECTION_COLUMN_WIDTH_PX,
		minSize: SELECTION_COLUMN_WIDTH_PX,
		maxSize: SELECTION_COLUMN_WIDTH_PX,
	};
}
